'use es6';

import { Map as ImmutableMap, OrderedMap, List } from 'immutable';
import SearchResultRecord from 'SalesContentIndexUI/data/records/SearchResultRecord';
import SearchQueryRecord from 'SalesContentIndexUI/data/records/SearchQueryRecord';
import SearchStatus from 'SalesContentIndexUI/data/constants/SearchStatus';
import { SET_SEARCH_QUERY, SET_SORT, SET_SELECTED_FOLDER, SET_VIEW_FILTER, SEARCH_FETCH_SUCCEEDED, SEARCH_FETCH_FAILED, SET_FROM_MODAL, SET_FROM_QUERY_PARAMS, ADD_TEMP_RESULT, UPDATE_RESULT, REMOVE_RESULT, REMOVE_RESULTS, REPLACE_RESULT, REPLACE_RESULT_AFTER_SEARCH, FOLDER_FETCH_SUCCEEDED } from 'SalesContentIndexUI/data/constants/ActionTypes';
const setSearchResults = (results, afterSearchReplacements = ImmutableMap()) => results.reduce((searchResultsMap, result) => {
  const searchResult = SearchResultRecord.init(result);
  return searchResultsMap.set(searchResult.id, afterSearchReplacements.get(searchResult.id, searchResult));
}, OrderedMap());
const removeResults = (deletedSearchResultIds, results) => deletedSearchResultIds.reduce((updatedSearchResults, id) => updatedSearchResults.remove(id), results);
const replaceResults = (idToReplace, replacementResult, results) => results.reduce((newResultsMap, result) => {
  const resultToUse = result.id === idToReplace ? replacementResult : result;
  return newResultsMap.set(resultToUse.id, resultToUse);
}, OrderedMap());
export default (({
  defaultViewFilter,
  allContentFilterList,
  looseItemContentType,
  folderContentType,
  options
}) => {
  const {
    currentSort,
    searchLimit
  } = options;
  const initialState = {
    isInitializing: true,
    defaultViewFilter,
    allContentFilterList,
    selectedViewFilter: defaultViewFilter,
    currentSort,
    mostRecentSort: currentSort,
    searchResults: OrderedMap(),
    afterSearchReplacements: ImmutableMap(),
    tempSearchResults: OrderedMap(),
    updatedResults: OrderedMap(),
    searchQuery: new SearchQueryRecord({
      offset: 0,
      limit: searchLimit,
      filters: defaultViewFilter.getFilters(),
      sorts: List([currentSort]),
      contentTypesToHydrate: folderContentType ? List([looseItemContentType, folderContentType]) : List([looseItemContentType])
    }),
    total: 0,
    searchStatus: SearchStatus.LOADING,
    selectedFolder: null
  };
  return (state = initialState, action) => {
    switch (action.type) {
      case SET_SEARCH_QUERY:
        return Object.assign({}, state, {
          tempSearchResults: OrderedMap(),
          searchQuery: action.payload,
          searchStatus: SearchStatus.LOADING,
          // Don't set mostRecentSort here as it is either undefined or itself
          currentSort: action.payload.sorts.first()
        });
      case SET_FROM_MODAL:
        {
          const {
            isInitial,
            searchQuery
          } = action.payload;
          return Object.assign({}, state, {
            searchQuery,
            tempSearchResults: !isInitial ? OrderedMap() : state.tempSearchResults,
            searchStatus: !isInitial ? SearchStatus.LOADING : state.searchStatus
          });
        }
      case SET_FROM_QUERY_PARAMS:
        {
          const {
            isInitial,
            searchQuery,
            selectedViewFilter,
            selectedFolder
          } = action.payload;
          return Object.assign({}, state, {
            searchQuery,
            selectedFolder,
            tempSearchResults: !isInitial ? OrderedMap() : state.tempSearchResults,
            selectedViewFilter: selectedViewFilter || state.selectedViewFilter,
            searchStatus: !isInitial ? SearchStatus.LOADING : state.searchStatus,
            mostRecentSort: searchQuery.sorts.first(),
            currentSort: searchQuery.sorts.first()
          });
        }
      case SET_SORT:
        {
          const {
            selectedSort,
            searchQuery
          } = action.payload;
          return Object.assign({}, state, {
            searchQuery,
            tempSearchResults: OrderedMap(),
            mostRecentSort: selectedSort,
            currentSort: selectedSort,
            searchStatus: SearchStatus.LOADING
          });
        }
      case SET_SELECTED_FOLDER:
        {
          const {
            selectedFolder,
            searchQuery
          } = action.payload;
          return Object.assign({}, state, {
            selectedFolder,
            searchQuery,
            tempSearchResults: OrderedMap(),
            searchStatus: SearchStatus.LOADING
          });
        }
      case SET_VIEW_FILTER:
        {
          const {
            selectedViewFilter,
            searchQuery
          } = action.payload;
          return Object.assign({}, state, {
            selectedViewFilter,
            searchQuery,
            tempSearchResults: OrderedMap(),
            mostRecentSort: selectedViewFilter.getSort(),
            currentSort: selectedViewFilter.getSort(),
            selectedFolder: null,
            searchStatus: SearchStatus.LOADING
          });
        }
      case SEARCH_FETCH_SUCCEEDED:
        {
          const {
            results,
            total
          } = action.payload.toObject();
          return Object.assign({}, state, {
            isInitializing: false,
            total,
            searchResults: setSearchResults(results, state.afterSearchReplacements),
            searchStatus: SearchStatus.SUCCEEDED,
            afterSearchReplacements: ImmutableMap()
          });
        }
      case SEARCH_FETCH_FAILED:
        return Object.assign({}, state, {
          isInitializing: false,
          searchStatus: SearchStatus.FAILED
        });
      case ADD_TEMP_RESULT:
        {
          const searchResult = action.payload;
          return Object.assign({}, state, {
            total: state.total + 1,
            tempSearchResults: OrderedMap({
              [searchResult.id]: searchResult
            }).merge(state.tempSearchResults)
          });
        }
      case UPDATE_RESULT:
        {
          const updatedSearchResult = action.payload;
          return Object.assign({}, state, {
            updatedResults: state.updatedResults.set(updatedSearchResult.id, updatedSearchResult)
          });
        }
      case REMOVE_RESULT:
        {
          const deletedSearchResultIds = List([action.payload]);
          return Object.assign({}, state, {
            total: state.total - 1,
            searchResults: removeResults(deletedSearchResultIds, state.searchResults),
            tempSearchResults: removeResults(deletedSearchResultIds, state.tempSearchResults)
          });
        }
      case REMOVE_RESULTS:
        {
          const deletedSearchResultIds = action.payload;
          return Object.assign({}, state, {
            total: state.total - deletedSearchResultIds.size,
            searchResults: removeResults(deletedSearchResultIds, state.searchResults),
            tempSearchResults: removeResults(deletedSearchResultIds, state.tempSearchResults)
          });
        }
      case REPLACE_RESULT:
        {
          const {
            idToReplace,
            replacementResult
          } = action.payload;
          return Object.assign({}, state, {
            searchResults: replaceResults(idToReplace, replacementResult, state.searchResults),
            tempSearchResults: replaceResults(idToReplace, replacementResult, state.tempSearchResults)
          });
        }
      case REPLACE_RESULT_AFTER_SEARCH:
        {
          const {
            idToReplace,
            replacementResult
          } = action.payload;
          return Object.assign({}, state, {
            searchResults: replaceResults(idToReplace, replacementResult, state.searchResults),
            tempSearchResults: replaceResults(idToReplace, replacementResult, state.tempSearchResults),
            afterSearchReplacements: state.afterSearchReplacements.set(idToReplace, replacementResult)
          });
        }
      case FOLDER_FETCH_SUCCEEDED:
        {
          return Object.assign({}, state, {
            selectedFolder: SearchResultRecord.init(action.payload)
          });
        }
      default:
        return state;
    }
  };
});